// src/pages/SettingsPage.js
import React, { useEffect, useState } from "react";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { Copy } from "lucide-react";

const SettingsPage = () => {
  const auth = getAuth();
  const db = getFirestore();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        window.location.href = "/signin";
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserData(userSnap.data());
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [auth, db]);

  const handleCopy = () => {
    if (userData?.referralId || userData?.referralCode) {
      const code = userData?.referralId || userData?.referralCode;
      navigator.clipboard.writeText(code);
      alert("Referral ID copied ✅");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = "/signin";
  };

  if (loading) return <p className="text-center py-10 text-gray-600">Loading...</p>;

  return (
    <div className="min-h-screen bg-gray-100 py-10">
      <div className="w-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col gap-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Settings</h1>

        {/* Profile Info */}
        <div className="bg-white shadow-xl rounded-2xl p-8 flex flex-col lg:flex-row items-start gap-6">
          <div className="flex items-center gap-6 flex-1">
            <div className="w-28 h-28 bg-blue-100 rounded-full flex items-center justify-center text-4xl font-bold text-blue-600">
              {userData?.name?.[0] || "U"}
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-800">{userData?.name}</h2>
              <p className="text-gray-600 mt-1">📧 {userData?.email}</p>
              <p className="flex items-center gap-2 mt-2 text-gray-600">
                🏷 Referral ID: <span className="font-mono">{userData?.referralId || userData?.referralCode}</span>
                <button
                  onClick={handleCopy}
                  className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Copy size={16} /> Copy
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Team Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="bg-white shadow-lg rounded-2xl p-6 text-center">
            <p className="text-3xl font-bold text-blue-600">{userData?.teamA || 0}</p>
            <p className="text-gray-500 mt-1">Team A</p>
          </div>
          <div className="bg-white shadow-lg rounded-2xl p-6 text-center">
            <p className="text-3xl font-bold text-green-600">{userData?.teamB || 0}</p>
            <p className="text-gray-500 mt-1">Team B</p>
          </div>
          <div className="bg-white shadow-lg rounded-2xl p-6 text-center">
            <p className="text-3xl font-bold text-red-600">{userData?.teamC || 0}</p>
            <p className="text-gray-500 mt-1">Team C</p>
          </div>
        </div>

        {/* Logout Button at the end */}
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition"
          >
            🚪 Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
